/*
 * Keeps Recharge's own cart-drawer subscription controls limited to the store's active plan,
 * so a shopper can never end up on a different plan than the one shown.
 *
 * Two controls are handled, each scoped to its own cart line so multiple lines never interfere
 * with each other: a plain "upgrade to subscription" button, and a frequency dropdown for a line
 * that's already subscribed. The post-checkout upsell popup is left alone for now — hiding its
 * options wouldn't stop it from applying the wrong plan anyway, so it needs a different fix.
 */
(() => {
  const HOST_PREFIX = 'recharge-storefront-experiences__';
  const ACTIVE_APP_GLOBAL = 'activeSubscriptionAppId';
  const DEBUG_FLAG = 'STAYAI_RECHARGE_DRAWER_GATE_DEBUG';
  const POLL_TIMEOUT_MS = 20000;
  const POLL_INTERVAL_MS = 300;
  const DEBOUNCE_MS = 30;
  const KEEP_VALUES = new Set(['ONETIME', '']);

  const P = (...a) => { if (window[DEBUG_FLAG]) console.log('>>>', ...a.map(v => (v && typeof v === 'object') ? JSON.stringify(v) : v)); };

  const productCache = new Map();
  function getProduct(handle) {
    if (!productCache.has(handle)) {
      productCache.set(handle, fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
        .then(r => r.json())
        .catch(e => { productCache.delete(handle); throw e; }));
    }
    return productCache.get(handle);
  }

  async function isProductActiveOnly(handle) {
    try {
      const product = await getProduct(handle);
      const groups = product.selling_plan_groups || [];
      if (groups.length === 0) return true;
      const activeId = String(window[ACTIVE_APP_GLOBAL] || '');
      return groups.every(g => String(g.app_id) === activeId);
    } catch (e) {
      P('product fetch failed, failing closed', handle, String(e));
      return false;
    }
  }

  async function activePlanIds(handle) {
    try {
      const product = await getProduct(handle);
      const activeId = String(window[ACTIVE_APP_GLOBAL] || '');
      const ids = new Set();
      (product.selling_plan_groups || []).forEach(g => {
        if (String(g.app_id) === activeId) (g.selling_plans || []).forEach(p => ids.add(String(p.id)));
      });
      return { ids, ok: true };
    } catch (e) {
      P('product fetch failed, failing closed on option filter', handle, String(e));
      return { ids: new Set(), ok: false };
    }
  }

  function ensureStyle(shadowRoot) {
    if (shadowRoot.querySelector('style[data-stayai-gate-style]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-stayai-gate-style', '');
    style.textContent = '[data-stayai-hidden]{display:none!important;}';
    shadowRoot.appendChild(style);
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.setAttribute('data-stayai-hidden', '');
      if ('disabled' in el) el.disabled = true;
    } else {
      el.removeAttribute('data-stayai-hidden');
      if ('disabled' in el) el.disabled = false;
    }
  }

  function findSubscribeButtonIn(root) {
    const buttons = [...root.querySelectorAll('button')];
    return buttons.find(b => /subscri/i.test(b.textContent || '') && !/continue/i.test(b.textContent || '')) || null;
  }

  function filterSelectOptions(select, ids, ok) {
    [...select.options].forEach(opt => {
      if (KEEP_VALUES.has(opt.value)) return;
      const foreign = !ok || !ids.has(String(opt.value));
      if (opt.disabled !== foreign) opt.disabled = foreign;
      if (opt.hidden !== foreign) opt.hidden = foreign;
    });
  }

  const scanTokens = new WeakMap();

  async function scanOverlay(shadowRoot) {
    return;
  }

  async function scanDrawer(shadowRoot) {
    const listItems = [...shadowRoot.querySelectorAll('[role="listitem"]')];
    const relevant = listItems.some(li => findSubscribeButtonIn(li) || li.querySelector('select[name="selling_plan"]'));
    if (!relevant) return;

    const myToken = (scanTokens.get(shadowRoot) || 0) + 1;
    scanTokens.set(shadowRoot, myToken);

    let cart;
    try {
      cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
    } catch (e) {
      P('cart fetch failed', String(e));
      return;
    }
    if (scanTokens.get(shadowRoot) !== myToken) return;

    // Matches each line to the cart by position, since the markup has no other stable id.
    for (let i = 0; i < listItems.length; i++) {
      const li = listItems[i];
      const item = cart.items[i];
      const handle = item && item.handle;
      const button = findSubscribeButtonIn(li);
      const select = li.querySelector('select[name="selling_plan"]');
      if (!handle || (!button && !select)) continue;

      if (button) {
        const safe = await isProductActiveOnly(handle);
        if (scanTokens.get(shadowRoot) !== myToken) return;
        ensureStyle(shadowRoot);
        setHidden(button, !safe);
        P('scan (gate)', { index: i, handle, safe });
      } else if (select) {
        const { ids, ok } = await activePlanIds(handle);
        if (scanTokens.get(shadowRoot) !== myToken) return;
        filterSelectOptions(select, ids, ok);
        P('scan (option-filter)', { index: i, handle, ok, activeCount: ids.size });
      }
    }
  }

  const debounceTimers = new WeakMap();
  function scheduleScan(shadowRoot, isOverlay) {
    clearTimeout(debounceTimers.get(shadowRoot));
    debounceTimers.set(shadowRoot, setTimeout(() => (isOverlay ? scanOverlay(shadowRoot) : scanDrawer(shadowRoot)), DEBOUNCE_MS));
  }

  function attach(host) {
    const shadowRoot = host.shadowRoot;
    if (!shadowRoot) { P('host found but no open shadow root', host.id); return; }
    const isOverlay = host.id.includes('cart-overlay');
    scheduleScan(shadowRoot, isOverlay);
    const observer = new MutationObserver(() => scheduleScan(shadowRoot, isOverlay));
    // Also watches for text changes, since a button's label can render after the button itself.
    observer.observe(shadowRoot, { childList: true, subtree: true, characterData: true });
    P('attached to', host.id, isOverlay ? '(overlay)' : '(drawer)');
  }

  // Matches any Recharge widget on the page, since more than one can be present at once.
  const attachedHosts = new WeakSet();
  function tryAttachAll() {
    document.querySelectorAll(`[id^="${HOST_PREFIX}"]`).forEach(host => {
      if (attachedHosts.has(host) || !host.shadowRoot) return;
      attachedHosts.add(host);
      attach(host);
    });
  }

  const start = Date.now();
  (function poll() {
    tryAttachAll();
    if (Date.now() - start > POLL_TIMEOUT_MS) { P('poll window elapsed, stopping'); return; }
    setTimeout(poll, POLL_INTERVAL_MS);
  })();
})();
